import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import RecentLinks from '../components/Dashboard/RecentLinks';
import { useStore } from '../store';
import { useAuth } from '../contexts/AuthContext';
import { FiLink, FiShield, FiTrendingUp, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';

const DashboardPage: React.FC = () => {
  const { recentUrls } = useStore();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Check if user just upgraded
    if (searchParams.get('upgraded') === 'true') {
      // Refresh user data to get updated tier
      refreshUser().then(() => {
        toast.success('🎉 Welcome to Pro! Your subscription is now active.');
      });
      // Remove the parameter from URL to prevent showing the toast again on refresh
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshUser]);

  const totalClicks = recentUrls.reduce((sum, url) => sum + url.clicks, 0);
  const activeLinks = recentUrls.filter(url => !url.expiresAt || new Date(url.expiresAt) > new Date()).length;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Manage your shortened URLs and view analytics</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Links</p>
              <p className="text-2xl font-bold text-gray-900">{recentUrls.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <FiLink className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Links</p>
              <p className="text-2xl font-bold text-gray-900">{activeLinks}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FiShield className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Clicks</p>
              <p className="text-2xl font-bold text-gray-900">{totalClicks}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FiTrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Click Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {recentUrls.length > 0 ? Math.round(totalClicks / recentUrls.length) : 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiClock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Links */}
      <RecentLinks />
    </div>
  );
};

export default DashboardPage;